import { Slot } from '@radix-ui/react-slot';
import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react';
import { forwardRef, type HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../utils';

interface ActionBarProps extends HTMLMotionProps<'div'> {
  show: boolean;
}

export const ActionBar = forwardRef<HTMLDivElement, ActionBarProps>(
  ({ children, show, className, ...props }, ref) => {
    return createPortal(
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ bottom: 0, opacity: 0 }}
            animate={{
              bottom: 50,
              opacity: 1,
              transition: {
                type: 'spring',
                stiffness: 500,
                damping: 25,
                mass: 1.4,
              },
            }}
            exit={{
              bottom: 0,
              opacity: 0,
              transition: {
                type: 'spring',
                stiffness: 500,
                damping: 30,
              },
            }}
            key={'action-bar'}
            className={cn(
              'dark flex flex-row items-center p-2 w-fit rounded-sm shadow-2xl bg-background text-foreground',
              'absolute left-1/2 -translate-x-1/2 z-[1000]',
              '[&>.action-bar-item]:rounded-sm',
              className,
            )}
            {...props}
            ref={ref}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  },
);

ActionBar.displayName = 'ActionBar';

export const ActionBarSeparator = () => (
  <div className={cn('action-bar-sep self-stretch w-[1px] bg-accent mx-2')} />
);

interface ActionBarItemProps extends HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

export const ActionBarItem = forwardRef<HTMLDivElement, ActionBarItemProps>(
  ({ children, className, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp
        className={cn(
          'action-bar-item flex flex-row gap-2 items-center px-4 py-2 self-stretch text-base',
          className,
        )}
        {...props}
        ref={ref}
      >
        {children}
      </Comp>
    );
  },
);
